import { formatDate } from '@angular/common';

import type {
  ReportConfig,
  SheetConfig,
} from '@app/services/reporting';

export interface MailshotDetailUser {
  readonly name: string;
  readonly profileId: number;
  readonly dealerId: number;
  readonly downloadDate: string | number | Date | null | undefined;
}

export interface MailshotDetail {
  readonly name: string;
  readonly date: string | number | Date;
  readonly users: ReadonlyArray<MailshotDetailUser>;
}

export interface MailshotNameResolver {
  readonly getProfileName: (id: number) => string;
  readonly getDealersName: (id: number) => string;
}

interface MailshotRow {
  readonly name: string;
  readonly profile: string;
  readonly dealer: string;
  readonly downloadDate: string;
}

const REPORT_LOCALE = 'en-Us';
const HEADER_DATE_FORMAT = 'dd-MMM-yyyy, HH:mm';
const ROW_DATE_FORMAT = 'dd-MMM-yyyy';
const COLUMN_WIDTH = 40;

export function buildMailshotReportConfig(
  detail: MailshotDetail,
  resolver: MailshotNameResolver,
): ReportConfig {
  const headerDate = formatDate(
    detail.date,
    HEADER_DATE_FORMAT,
    REPORT_LOCALE,
  );

  const rows = detail.users.map((u) => mapUser(u, resolver));

  const sheet: SheetConfig<MailshotRow> = {
    name: 'Circular',
    // Preserve the legacy XLSX output 1:1 — no autoFilter dropdowns
    // on the header (the old generator never sorted) and no frozen
    // pane. Anything visual past pure data layout is opt-in per
    // report; mailshot stays vanilla.
    autoFilter: false,
    freezeHeader: false,
    preHeaderRows: [
      { values: [] },
      {
        values: [detail.name, '', '', headerDate],
        merge: 'A2:C2',
      },
      { values: [] },
    ],
    columns: [
      { key: 'name', header: 'Nombre', width: COLUMN_WIDTH },
      { key: 'profile', header: 'Perfil', width: COLUMN_WIDTH },
      { key: 'dealer', header: 'Distribuidor', width: COLUMN_WIDTH },
      {
        key: 'downloadDate',
        header: 'Fecha de descarga',
        width: COLUMN_WIDTH,
      },
    ],
    rows,
  };

  return {
    fileName: `detalle_${detail.name}`,
    sheets: [sheet],
  };
}

function mapUser(
  user: MailshotDetailUser,
  resolver: MailshotNameResolver,
): MailshotRow {
  return {
    name: user.name,
    profile: resolver.getProfileName(user.profileId),
    dealer: resolver.getDealersName(user.dealerId),
    downloadDate: user.downloadDate
      ? formatDate(user.downloadDate, ROW_DATE_FORMAT, REPORT_LOCALE)
      : '',
  };
}
